import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Typography, Box, Divider, Tooltip
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import HistoryIcon from '@mui/icons-material/History';
import StoreIcon from '@mui/icons-material/Store';
import LinkIcon from '@mui/icons-material/Link';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import './SmartProductCard.css';

const formatCurrency = (amount) => {
    if (amount == null || amount === '') return 'N/A';
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount);
};

const SmartProductCard = ({ product }) => {
    const navigate = useNavigate();
    const [openModal, setOpenModal] = useState(false);

    // --- 1. DETERMINE SOURCE & BADGE ---
    const isLinked = product.isOmsListed && product.ecomProductId;
    const isCatalogOnly = product.source === 'ECOM_ONLY';

    // Use ecomProductId as ESIN, default to 'n/a'
    const displayEsin = product.ecomProductId ? product.ecomProductId : 'n/a';

    let badgeLabel = "Internal History";
    let variant = "internal";
    let badgeIcon = <HistoryIcon fontSize="inherit" />;

    if (isLinked) {
        badgeLabel = "Linked Product";
        variant = "linked";
        badgeIcon = <LinkIcon fontSize="inherit" />;
    } else if (isCatalogOnly) {
        badgeLabel = "Catalog Item";
        variant = "catalog";
        badgeIcon = <StoreIcon fontSize="inherit" />;
    }

    const imageUrl = product.ecomThumbnailUrl || "https://via.placeholder.com/150?text=No+Image";
    const hasBrand = product.brandName && product.brandName !== 'N/A';

    const handleViewDetails = () => {
        if (product.ecomProductId) {
            navigate(`/smart-search/product/${product.ecomProductId}`, { state: { product } });
        } else {
            setOpenModal(true);
        }
    };

    return (
        <>
            <div className={`smart-card smart-card--${variant}`}>
                <div className="smart-card-media" onClick={handleViewDetails} role="presentation">
                    <img src={imageUrl} alt={product.productName} className="smart-card-img" loading="lazy" />

                    <div className="smart-card-pills">
                        <span className={`smart-pill smart-pill--${variant}`}>
                            {badgeIcon}
                            {badgeLabel}
                        </span>

                        {product.purchaseCount != null && (
                            <Tooltip
                                arrow
                                placement="top"
                                title={`Purchased ${product.purchaseCount} ${product.purchaseCount === 1 ? 'time' : 'times'}`}
                            >
                                <span
                                    className="smart-pill smart-pill--count"
                                    aria-label={`Purchased ${product.purchaseCount} ${product.purchaseCount === 1 ? 'time' : 'times'}`}
                                >
                                    <ShoppingCartIcon fontSize="inherit" />
                                    {product.purchaseCount}
                                </span>
                            </Tooltip>
                        )}
                    </div>
                </div>

                <div className="smart-card-body">
                    <div className="smart-card-meta">
                        <span className="smart-category">{product.omsCategory || 'Uncategorized'}</span>
                        <span className="smart-esin">ESIN {displayEsin}</span>
                    </div>

                    <h3 className="smart-title" title={product.productName}>
                        {product.productName}
                    </h3>

                    <div className="smart-brand">
                        {hasBrand ? <>Brand: <strong>{product.brandName}</strong></> : '\u00A0'}
                    </div>

                    <div className="smart-stats">
                        <div className="smart-stat">
                            <span className="smart-stat-label">Last PP</span>
                            <span className="smart-stat-value smart-stat-value--accent">
                                {formatCurrency(product.lastPurchasePrice)}
                            </span>
                        </div>
                        <div className="smart-stat">
                            <span className="smart-stat-label">MRP</span>
                            <span className="smart-stat-value">{formatCurrency(product.ecomMrp)}</span>
                        </div>
                    </div>

                    <div className="smart-vendor" title={product.lastVendor || undefined}>
                        <StorefrontIcon fontSize="inherit" />
                        <span>{product.lastVendor || 'No vendor on record'}</span>
                    </div>

                    <button type="button" className="smart-card-cta" onClick={handleViewDetails}>
                        {product.ecomProductId ? "View Full Details" : "View OMS Info"}
                        <ArrowForwardIcon fontSize="inherit" />
                    </button>
                </div>
            </div>

            {/* --- DETAILS MODAL --- */}
            <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InfoIcon color="primary" /> OMS Item Details
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary">Item Name</Typography>
                        <Typography variant="h6">{product.productName}</Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="textSecondary">ESIN:</Typography>
                        <Typography variant="body2" fontWeight="bold" fontFamily="monospace">
                            {displayEsin}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Brand:</Typography>
                        <Typography variant="body2" fontWeight="bold">
                            {product.brandName || 'N/A'}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Category:</Typography>
                        <Typography variant="body2" fontWeight="bold">{product.omsCategory}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Vendor:</Typography>
                        <Typography variant="body2" fontWeight="bold">{product.lastVendor}</Typography>
                    </Box>

                    {/* Price Section */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Last Purchase Price:</Typography>
                        <Typography variant="body2" fontWeight="bold" color="primary">
                            {formatCurrency(product.lastPurchasePrice)}
                        </Typography>
                    </Box>

                    {/* FIX: Added MRP to Modal */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">MRP:</Typography>
                        <Typography variant="body2" fontWeight="bold">
                            {formatCurrency(product.ecomMrp)}
                        </Typography>
                    </Box>

                    <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="caption" display="block" color="textSecondary" gutterBottom>
                            RAW DETAILS / REMARKS:
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {product.ecomDescription || product.description || "No additional details available."}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenModal(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default SmartProductCard;